import type { AxiosError } from 'axios';

/**
 * API 에러 응답 타입 (자체 API 형식)
 */
export interface ApiErrorResponse {
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

/**
 * FastAPI HTTPException 응답 타입
 */
export interface FastAPIErrorResponse {
  detail: string | Record<string, unknown>;
}

/**
 * 표준화된 API 에러 클래스
 *
 * Axios 에러를 일관된 형식으로 변환하여 처리
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: Record<string, unknown>;
  /** FastAPI HTTPException의 원본 detail 객체 */
  readonly data?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, unknown>,
    data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.data = data;
  }

  /**
   * Axios 에러를 ApiError로 변환
   */
  static fromAxiosError(error: AxiosError<ApiErrorResponse | FastAPIErrorResponse>): ApiError {
    const status = error.response?.status ?? 0;
    const responseData = error.response?.data;

    // FastAPI HTTPException 형식: { detail: { code, message, ... } }
    if (responseData && 'detail' in responseData) {
      const detail = responseData.detail;
      if (typeof detail === 'object' && detail !== null) {
        const detailObj = detail as Record<string, unknown>;
        return new ApiError(
          (detailObj.message as string) ?? error.message ?? '알 수 없는 오류가 발생했습니다',
          status,
          detailObj.code as string | undefined,
          undefined,
          detailObj,
        );
      }
      // detail이 문자열인 경우
      return new ApiError(String(detail), status, undefined, undefined, { detail });
    }

    // 자체 API 형식: { message, code, details }
    const data = responseData as ApiErrorResponse | undefined;
    return new ApiError(
      data?.message ?? error.message ?? '알 수 없는 오류가 발생했습니다',
      status,
      data?.code,
      data?.details,
    );
  }

  /**
   * 네트워크 에러인지 확인
   */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /**
   * 인증 에러인지 확인
   */
  get isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * 권한 에러인지 확인
   */
  get isForbiddenError(): boolean {
    return this.status === 403;
  }

  /**
   * 서버 에러인지 확인
   */
  get isServerError(): boolean {
    return this.status >= 500;
  }
}
